import React, { useState } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormGroup
} from '@mui/material';
import {
  Security as SecurityIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  Folder as ModuleIcon,
  FolderOpen as FeatureIcon,
  Description as SubFeatureIcon,
  Label as SectionIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { rolesAPI, permissionsAPI } from '../../shared/services/api';
import toast from 'react-hot-toast';

const PermissionSetup = () => {
  const queryClient = useQueryClient();
  const [selectedPortal, setSelectedPortal] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [permissions, setPermissions] = useState({});

  // Fetch all roles
  const { data: rolesData, isLoading, error, refetch } = useQuery({
    queryKey: ['roles'],
    queryFn: rolesAPI.getRoles,
  });

  // Fetch permissions for the selected portal
  const { data: permissionsData, isLoading: permissionsDataLoading } = useQuery({
    queryKey: ['permissions', selectedPortal],
    queryFn: () => permissionsAPI.getPermissions({ portal: selectedPortal }),
    enabled: !!selectedPortal,
  });

  // Fetch role data for selected role
  const { data: roleData, isLoading: roleDataLoading } = useQuery({
    queryKey: ['role', selectedRole],
    queryFn: () => rolesAPI.getRole(selectedRole),
    enabled: !!selectedRole,
  });

  // Available portals
  const portals = ['member', 'corporate', 'provider', 'intermediary', 'insurer', 'irda'];

  // Filter roles based on selected portal
  console.log('Roles data:', rolesData?.data?.data);
  const filteredRoles = rolesData?.data?.data?.filter(role => {
    if (!selectedPortal) return false;
    return role.portalAccess?.includes(selectedPortal) || role.userType === selectedPortal;
  }) || [];

  // Reset role selection when portal changes
  const handlePortalChange = (portalValue) => {
    setSelectedPortal(portalValue);
    setSelectedRole('');
    setPermissions({});
  };

  // Helper function to ensure permissions is always an object
  const safePermissions = permissions || {};

  // Update role permissions mutation
  const updatePermissionsMutation = useMutation({
    mutationFn: ({ id, permissions }) => {
      console.log('API call with:', { id, permissions });

      // Build the nested structure directly from the permission data structure
      // instead of trying to parse concatenated strings
      const nestedPermissions = {};

      // Process each enabled permission key by looking up the actual permission structure
      Object.keys(permissions).forEach(key => {
        if (permissions[key]) {
          // Find the permission structure that matches this key
          if (permissionsData?.data?.data) {
            permissionsData.data.data.forEach(permission => {
              permission.features?.forEach(feature => {
                feature.subFeatures?.forEach(subFeature => {
                  subFeature.sections?.forEach(section => {
                    const sectionKey = `${permission.name}-${feature.name}-${subFeature.name}-${section.name}`;

                    if (sectionKey === key) {
                      // Build nested structure in the requested format:
                      // MODULE: [{ FEATURE1: [{ SUBFEATURE1: [SECTION1, SECTION2] }], FEATURE2: [...] }]
                      const module = permission.name;
                      const featureName = feature.name;
                      const subFeatureName = subFeature.name;
                      const sectionName = section.name;

                      // Initialize module if not exists
                      if (!nestedPermissions[module]) {
                        nestedPermissions[module] = [{}]; // Initialize with a single object to hold all features
                      }

                      // Get the main features object (first and only object in the module array)
                      const moduleObj = nestedPermissions[module][0];

                      // Find or create feature array within the module object
                      if (!moduleObj[featureName]) {
                        moduleObj[featureName] = [];
                      }

                      // Find or create sub-feature object within the feature array
                      let subFeatureObj = moduleObj[featureName].find(sf => sf.hasOwnProperty(subFeatureName));
                      if (!subFeatureObj) {
                        subFeatureObj = { [subFeatureName]: [] };
                        moduleObj[featureName].push(subFeatureObj);
                      }

                      // Add section to the sub-feature array if not already present
                      if (!subFeatureObj[subFeatureName].includes(sectionName)) {
                        subFeatureObj[subFeatureName].push(sectionName);
                      }
                    }
                  });
                });
              });
            });
          }
        }
      });

      console.log('Built nested permissions:', nestedPermissions);

      // Convert permissions to the assignedPermissions format with hierarchical structure
      const assignedPermissions = [];
      if (permissionsData?.data?.data) {
        permissionsData.data.data.forEach(permission => {
          const access = {};
          let hasAccess = false;

          permission.features?.forEach(feature => {
            feature.subFeatures?.forEach(subFeature => {
              subFeature.sections?.forEach(section => {
                const key = `${permission.name}-${feature.name}-${subFeature.name}-${section.name}`;
                if (permissions[key]) {
                  // Store the permission with hierarchical metadata for proper key-value structure
                  access[key] = {
                    enabled: permissions[key],
                    module: permission.name,
                    feature: feature.name,
                    subFeature: subFeature.name,
                    section: section.name,
                    // Store the original permission structure for reference
                    permissionId: permission._id,
                    featureId: feature._id,
                    subFeatureId: subFeature._id,
                    sectionId: section._id
                  };
                  hasAccess = true;
                }
              });
            });
          });

          if (hasAccess) {
            assignedPermissions.push({
              permission: permission._id,
              access: access
            });
          }
        });
      }

      // Update role with nested permissions format
      return rolesAPI.updateRole(id, {
        hierarchicalPermissions: nestedPermissions,
        assignedPermissions: assignedPermissions
      });
    },
    onSuccess: (data) => {
      console.log('Success response:', data);
      queryClient.invalidateQueries(['roles']);
      queryClient.invalidateQueries(['role', selectedRole]);
      toast.success('Permissions updated successfully');
    },
    onError: (error) => {
      console.error('Error updating permissions:', error);
      toast.error(error.response?.data?.message || 'Failed to update permissions');
    },
  });

  const roles = rolesData?.data || [];

  // Initialize permissions when role is selected or role data is loaded
  React.useEffect(() => {
    if (roleData?.data?.data?.hierarchicalPermissions) {
      console.log('Loading hierarchical permissions from role:', roleData.data.data.hierarchicalPermissions);

      const hierarchicalPerms = roleData.data.data.hierarchicalPermissions;

      // Check if it's the new nested format or old flat format
      const isNestedFormat = Object.values(hierarchicalPerms).some(value => Array.isArray(value));

      if (isNestedFormat) {
        // Convert nested format back to flat format for UI
        console.log('Converting nested permissions to flat format for UI');
        const flatPermissions = {};

        Object.keys(hierarchicalPerms).forEach(module => {
          if (Array.isArray(hierarchicalPerms[module])) {
            hierarchicalPerms[module].forEach(featureObj => {
              Object.keys(featureObj).forEach(feature => {
                if (Array.isArray(featureObj[feature])) {
                  featureObj[feature].forEach(subFeatureObj => {
                    Object.keys(subFeatureObj).forEach(subFeature => {
                      if (Array.isArray(subFeatureObj[subFeature])) {
                        subFeatureObj[subFeature].forEach(section => {
                          const key = `${module}-${feature}-${subFeature}-${section}`;
                          flatPermissions[key] = true;
                        });
                      }
                    });
                  });
                }
              });
            });
          }
        });

        console.log('Converted flat permissions:', flatPermissions);
        setPermissions(flatPermissions);
      } else {
        // Old flat format - use as is
        console.log('Using flat permissions format');
        setPermissions(hierarchicalPerms || {});
      }
    } else if (roleData?.data?.data?.assignedPermissions) {
      console.log('Loading assigned permissions from role:', roleData.data.data.assignedPermissions);
      // Convert assigned permissions to the format expected by the UI
      const convertedPermissions = {};
      roleData.data.data.assignedPermissions.forEach(ap => {
        if (ap.access) {
          Object.keys(ap.access).forEach(key => {
            // Handle both old and new formats
            const accessValue = ap.access[key];
            if (typeof accessValue === 'object' && accessValue.enabled !== undefined) {
              // New format with hierarchical metadata
              convertedPermissions[key] = accessValue.enabled;
            } else {
              // Old format - direct boolean value
              convertedPermissions[key] = accessValue;
            }
          });
        }
      });
      setPermissions(convertedPermissions);
    } else if (selectedRole) {
      // Reset permissions when role changes but data hasn't loaded yet
      console.log('Resetting permissions for role:', selectedRole);
      setPermissions({});
    }
  }, [selectedRole, roleData]);

  // Save permissions
  const handleSavePermissions = () => {
    if (selectedRole) {
      console.log('Saving permissions:', { id: selectedRole, permissions });
      console.log('Selected role data:', filteredRoles.find(r => r._id === selectedRole));
      updatePermissionsMutation.mutate({
        id: selectedRole,
        permissions
      });
    }
  };

  // Test function to add some sample permissions
  const handleTestPermissions = () => {
    const testPermissions = {
      dashboard: {
        enabled: true,
        components: {
          analytics: true,
          charts: true,
          stats: false,
          notifications: true
        }
      },
      users: {
        enabled: false,
        components: {
          userList: true,
          userForm: false
        }
      }
    };
    console.log('Setting test permissions:', testPermissions);
    setPermissions(testPermissions);
  };

  if (isLoading || (selectedRole && roleDataLoading) || (selectedPortal && permissionsDataLoading)) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          {isLoading ? 'Loading roles...' : 'Loading permissions...'}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Failed to load roles: {error.message}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, width: '100%' }}>
        <Box >
          <Typography variant="h4" gutterBottom>
            Permission Setup
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Configure page and component-level permissions for roles
          </Typography>
        </Box>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
            sx={{ mr: 2 }}
          >
            Refresh
          </Button>
          {selectedRole && (
            <>
              <Button
                variant="outlined"
                onClick={handleTestPermissions}
                sx={{ mr: 2 }}
              >
                Test Permissions
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSavePermissions}
                disabled={updatePermissionsMutation.isPending}
              >
                {updatePermissionsMutation.isPending ? <CircularProgress size={20} /> : 'Save Permissions'}
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Portal and Role Selection */}
      <Grid container spacing={3} columns={12}>
        <Grid size={{xs: 12, sm: 12, md: 12}}>
          <Card>
            <CardHeader
              title="Select Portal & Role"
              subheader="Choose a portal first, then select a role to configure permissions"
              avatar={<SecurityIcon />}
            />
            <CardContent>
              {/* Portal Selection */}
              <FormControl sx={{ mr:3, mb: 2, width: '49%'}} >
                <InputLabel>Portal</InputLabel>
                <Select
                  value={selectedPortal}
                  label="Portal"
                  onChange={(e) => handlePortalChange(e.target.value)}
                >
                  {portals.map((portal) => (
                    <MenuItem key={portal} value={portal}>
                      {portal.charAt(0).toUpperCase() + portal.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Role Selection - Only show when portal is selected */}
              {selectedPortal && (
                <>
                  <FormControl sx={{ mb: 2, width: '49%' }}>
                    <InputLabel>Role</InputLabel>
                    <Select
                      value={selectedRole}
                      label="Role"
                      onChange={(e) => setSelectedRole(e.target.value)}
                      disabled={filteredRoles.length === 0}
                    >
                      {filteredRoles.map((role) => (
                        <MenuItem key={role._id} value={role._id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                            <Typography variant="body2" sx={{ flexGrow: 1 }}>
                              {role.name}
                            </Typography>
                            <Chip
                              label={role.userType}
                              size="small"
                              variant="outlined"
                              sx={{ ml: 1 }}
                            />
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {filteredRoles.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                      No roles found for the selected portal. Roles need to have portal access configured.
                    </Typography>
                  )}
                </>
              )}

              {selectedRole && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Selected Portal: {selectedPortal.charAt(0).toUpperCase() + selectedPortal.slice(1)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Selected Role: {filteredRoles.find(r => r._id === selectedRole)?.name}
                  </Typography>

                  {/* Permission Summary */}
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Permission Summary:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                      {console.log('Permissions:', safePermissions)}
                      {Object.keys(safePermissions).map((sectionKey) => {
                        const parts = sectionKey.split('-');
                        const sectionName = parts[parts.length - 1];
                        return (
                          <Chip
                            key={sectionKey}
                            label={sectionName.charAt(0).toUpperCase() + sectionName.slice(1).replace(/-/g, ' ')}
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        );
                      })}
                      {Object.keys(safePermissions).length === 0 && (
                        <Typography variant="caption" color="text.secondary">
                          No permissions configured
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Total sections enabled: {Object.keys(safePermissions).length}
                    </Typography>
                  </Box>

                  {updatePermissionsMutation.isPending && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="caption" sx={{ ml: 1 }}>
                        Saving permissions...
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Permission Tree */}
        <Grid size={{xs: 12, sm: 12, md: 12}}>
          {selectedRole ? (
            <Card>
              <CardHeader
                title="Hierarchical Permissions"
                subheader="Configure permissions by modules, features, sub-features, and sections"
              />
              <CardContent>
                <Box sx={{ maxHeight: '600px', overflow: 'auto' }}>
                  {permissionsData?.data?.data?.map((permission) => {
                    // Calculate module-level permission state
                    const modulePermissions = [];
                    permission.features?.forEach(feature => {
                      feature.subFeatures?.forEach(subFeature => {
                        subFeature.sections?.forEach(section => {
                          const sectionKey = `${permission.name}-${feature.name}-${subFeature.name}-${section.name}`;
                          modulePermissions.push(sectionKey);
                        });
                      });
                    });

                    const enabledModulePermissions = modulePermissions.filter(key => safePermissions[key]);
                    const isModuleFullyChecked = modulePermissions.length > 0 && enabledModulePermissions.length === modulePermissions.length;
                    const isModuleIndeterminate = enabledModulePermissions.length > 0 && enabledModulePermissions.length < modulePermissions.length;

                    const handleModuleChange = (checked) => {
                      const newPermissions = { ...safePermissions };
                      modulePermissions.forEach(key => {
                        if (checked) {
                          newPermissions[key] = true;
                        } else {
                          delete newPermissions[key];
                        }
                      });
                      setPermissions(newPermissions);
                    };

                    return (
                      <Accordion key={permission._id} sx={{ mb: 1 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                            <Checkbox
                              checked={isModuleFullyChecked}
                              indeterminate={isModuleIndeterminate}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleModuleChange(e.target.checked);
                              }}
                              sx={{ mr: 1 }}
                            />
                            <ModuleIcon sx={{ mr: 2, color: 'primary.main' }} />
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>
                              {permission.displayName}
                            </Typography>
                            <Chip
                              label={`${permission.features?.length || 0} features`}
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                        </AccordionSummary>
                      <AccordionDetails>
                        {/* Features */}
                        {permission.features?.map((feature) => {
                          // Calculate feature-level permission state
                          const featurePermissions = [];
                          feature.subFeatures?.forEach(subFeature => {
                            subFeature.sections?.forEach(section => {
                              const sectionKey = `${permission.name}-${feature.name}-${subFeature.name}-${section.name}`;
                              featurePermissions.push(sectionKey);
                            });
                          });

                          const enabledFeaturePermissions = featurePermissions.filter(key => safePermissions[key]);
                          const isFeatureFullyChecked = featurePermissions.length > 0 && enabledFeaturePermissions.length === featurePermissions.length;
                          const isFeatureIndeterminate = enabledFeaturePermissions.length > 0 && enabledFeaturePermissions.length < featurePermissions.length;

                          const handleFeatureChange = (checked) => {
                            const newPermissions = { ...safePermissions };
                            featurePermissions.forEach(key => {
                              if (checked) {
                                newPermissions[key] = true;
                              } else {
                                delete newPermissions[key];
                              }
                            });
                            setPermissions(newPermissions);
                          };

                          return (
                            <Accordion key={feature._id} sx={{ mb: 1 }}>
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                  <Checkbox
                                    checked={isFeatureFullyChecked}
                                    indeterminate={isFeatureIndeterminate}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      handleFeatureChange(e.target.checked);
                                    }}
                                    sx={{ mr: 1 }}
                                  />
                                  <FeatureIcon sx={{ mr: 2, color: 'secondary.main' }} />
                                  <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                                    {feature.displayName}
                                  </Typography>
                                  <Chip
                                    label={`${feature.subFeatures?.length || 0} sub-features`}
                                    size="small"
                                    variant="outlined"
                                  />
                                </Box>
                              </AccordionSummary>
                            <AccordionDetails>
                              {/* Sub-features */}
                              {feature.subFeatures?.map((subFeature) => {
                                // Calculate sub-feature-level permission state
                                const subFeaturePermissions = [];
                                subFeature.sections?.forEach(section => {
                                  const sectionKey = `${permission.name}-${feature.name}-${subFeature.name}-${section.name}`;
                                  subFeaturePermissions.push(sectionKey);
                                });

                                const enabledSubFeaturePermissions = subFeaturePermissions.filter(key => safePermissions[key]);
                                const isSubFeatureFullyChecked = subFeaturePermissions.length > 0 && enabledSubFeaturePermissions.length === subFeaturePermissions.length;
                                const isSubFeatureIndeterminate = enabledSubFeaturePermissions.length > 0 && enabledSubFeaturePermissions.length < subFeaturePermissions.length;

                                const handleSubFeatureChange = (checked) => {
                                  const newPermissions = { ...safePermissions };
                                  subFeaturePermissions.forEach(key => {
                                    if (checked) {
                                      newPermissions[key] = true;
                                    } else {
                                      delete newPermissions[key];
                                    }
                                  });
                                  setPermissions(newPermissions);
                                };

                                return (
                                  <Box key={subFeature._id} sx={{ mb: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                      <Checkbox
                                        checked={isSubFeatureFullyChecked}
                                        indeterminate={isSubFeatureIndeterminate}
                                        onChange={(e) => handleSubFeatureChange(e.target.checked)}
                                        sx={{ mr: 1 }}
                                      />
                                      <SubFeatureIcon sx={{ mr: 2, color: 'info.main' }} />
                                      <Typography variant="body1" sx={{ flexGrow: 1, fontWeight: 'medium' }}>
                                        {subFeature.displayName}
                                      </Typography>
                                      <Chip
                                        label={`${subFeature.sections?.length || 0} sections`}
                                        size="small"
                                        variant="outlined"
                                      />
                                    </Box>

                                  {/* Sections */}
                                  <Box sx={{ ml: 4 }}>
                                    <FormGroup>
                                      {subFeature.sections?.map((section) => {
                                        const sectionKey = `${permission.name}-${feature.name}-${subFeature.name}-${section.name}`;
                                        return (
                                          <FormControlLabel
                                            key={section._id}
                                            control={
                                              <Checkbox
                                                checked={safePermissions[sectionKey] || false}
                                                onChange={(e) => {
                                                  const newPermissions = { ...safePermissions };
                                                  if (e.target.checked) {
                                                    newPermissions[sectionKey] = true;
                                                  } else {
                                                    delete newPermissions[sectionKey];
                                                  }
                                                  setPermissions(newPermissions);
                                                }}
                                                size="small"
                                              />
                                            }
                                            label={
                                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <SectionIcon sx={{ mr: 1, fontSize: 16, color: 'success.main' }} />
                                                <Typography variant="body2">{section.displayName}</Typography>
                                              </Box>
                                            }
                                          />
                                        );
                                      })}
                                    </FormGroup>

                                    {(!subFeature.sections || subFeature.sections.length === 0) && (
                                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                        No sections available
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              );
                              })}

                              {(!feature.subFeatures || feature.subFeatures.length === 0) && (
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                  No sub-features available
                                </Typography>
                              )}
                            </AccordionDetails>
                          </Accordion>
                        );
                        })}

                        {(!permission.features || permission.features.length === 0) && (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            No features available
                          </Typography>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                  })}

                  {(!permissionsData?.data?.data || permissionsData.data.data.length === 0) && (
                    <Alert severity="info">
                      No permissions available for the selected portal. Please create permission modules first.
                    </Alert>
                  )}
                </Box>
              </CardContent>
            </Card>
          ) : (
            <Card sx={{width: '100%'}}>
              <CardContent>
                <Alert severity="info">
                  Please select a role to configure its permissions.
                </Alert>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {roles.length === 0 && (
        <Alert severity="info" sx={{ mt: 3 }}>
          No roles found. Create roles first to configure their permissions.
        </Alert>
      )}

    </Box>
  );
};

export default PermissionSetup;
